import AppActions from "../actions/AppActions";
import config from "../config/app";
import session from "../config/session";
import AppConstants from "../constants/AppConstants";

/**
 * @fileoverview Auth/session token lifecycle + the cross-tab session-validity check, extracted from the deleted `UserStore`. A thin layer over `src/config/session.js` (the API's token/userId holder) plus the `TOKEN_KEY`/`SITE_KEY` localStorage persistence. The boot cascade (`src/boot/accountBoot.js`) drives these; `BaseService` reads the token off `session` directly.
 */

const { TOKEN_KEY, SITE_KEY } = AppConstants;

/** @returns {string|null} the current session token (parity with `UserStore.getToken`). */
export const getToken = () => session.token;

/** @returns {string|null} the persisted token from localStorage. */
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);

/**
 * Persists the auth token to localStorage, unless in demo mode.
 * @param {string} token
 */
export const setLocalStorageToken = (token) => {
  if (!config.demoMode) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/** Removes the persisted site + token keys. Was `UserStore._clearLocalStorage`. */
export const clearLocalStorage = () => {
  localStorage.removeItem(SITE_KEY);
  localStorage.removeItem(TOKEN_KEY);
};

/** Sets the in-memory session token (consumed by `BaseService`/API requests). */
export const setSessionToken = (token) => {
  session.token = token;
};

/** Sets the in-memory session user id. */
export const setSessionUserId = (userId) => {
  session.userId = userId;
};

/** Clears all auth state on logout: nulls the session token and drops the persisted keys. */
export const clearAuth = () => {
  session.token = null;
  clearLocalStorage();
};

/** Cross-tab session-validity check. */
export const handlePageVisibility = () => {
  if (!document.hidden && !config.demoMode) {
    const token = session.token;
    const storageToken = localStorage.getItem(TOKEN_KEY);

    if (token !== storageToken) {
      AppActions.showAppError({
        userMessage:
          "This tab is no longer logged in. Showit is only supported in one tab at a time. Please close this tab.",
        hideBtn: true,
        userMessageOnly: true,
      });
    }
  }
};

let visibilityRegistered = false;

/** Registers the cross-tab `visibilitychange` listener. */
export const registerVisibilityHandler = () => {
  if (visibilityRegistered) return;
  visibilityRegistered = true;
  document.addEventListener("visibilitychange", handlePageVisibility, false);
};

/** Test-only: undo `registerVisibilityHandler` so each test starts clean. */
export const _resetVisibilityRegistrationForTest = () => {
  document.removeEventListener("visibilitychange", handlePageVisibility, false);
  visibilityRegistered = false;
};
